import React, { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "./ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Settings,
  Image as ImageIcon,
  Link2,
  Plus,
  Trash2,
  Save,
  Loader2,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  TestTube,
  Activity,
  RotateCcw,
  Edit2,
  Check,
  X,
  Camera
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

// Import default data
import { TRANSFORMER_DATA } from "../data/transformerData";
import { SWITCHGEAR_DATA } from "../data/switchgearData";
import { MOTOR_DATA } from "../data/motorData";
import { CABLE_DATA } from "../data/cableData";

const DEFAULT_DATA = {
  transformers: TRANSFORMER_DATA,
  switchgear: SWITCHGEAR_DATA,
  motors: MOTOR_DATA,
  cables: CABLE_DATA
};

const SEVERITY_OPTIONS = ["Critical", "Moderate", "Low"];
const COMPLEXITY_OPTIONS = ["Simple", "Standard", "Advanced"];
const COST_OPTIONS = ["$", "$$", "$$$"];

const AssetConfigManager = ({ 
  isOpen, 
  onClose, 
  assetType,
  onConfigSaved
}) => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("components");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [expandedComponents, setExpandedComponents] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [selectedComponentId, setSelectedComponentId] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalData, setEditModalData] = useState(null);

  // Load configuration on open
  useEffect(() => {
    if (isOpen && assetType) {
      loadConfig();
    } else if (!isOpen) {
      // Reset state when modal closes
      setSelectedComponentId(null);
      setActiveTab("components");
    }
  }, [isOpen, assetType]);

  // Initialize selectedComponentId when config loads
  useEffect(() => {
    if (config?.components?.length > 0 && !selectedComponentId) {
      setSelectedComponentId(config.components[0].id);
    }
  }, [config, selectedComponentId]);

  const loadConfig = async () => {
    setIsLoading(true);
    try {
      // Priority 1: Check asset-configs (user's customized version)
      const configResp = await axios.get(`${API}/asset-configs/${assetType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!configResp.data.is_default && configResp.data.components?.length > 0) {
        setConfig(configResp.data);
        setIsLoading(false);
        return;
      }
      
      // Priority 2: Check asset-types (admin-seeded data from new system)
      try {
        const atResp = await axios.get(`${API}/asset-types/${assetType}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (atResp.data && atResp.data.components?.length > 0) {
          setConfig({
            asset_type: assetType,
            components: atResp.data.components,
            is_default: false
          });
          setIsLoading(false);
          return;
        }
      } catch (e) {
        // asset-types endpoint may 404 for unknown slugs
      }
      
      // Priority 3: Use hardcoded default data
      const defaultData = DEFAULT_DATA[assetType] || [];
      setConfig({
        asset_type: assetType,
        components: defaultData,
        is_default: true
      });
    } catch (error) {
      console.error("Error loading config:", error);
      const defaultData = DEFAULT_DATA[assetType] || [];
      setConfig({
        asset_type: assetType,
        components: defaultData,
        is_default: true
      });
    }
    setIsLoading(false);
  };

  const saveConfig = async () => {
    setIsSaving(true);
    try {
      const response = await axios.put(`${API}/asset-configs/${assetType}`, {
        asset_type: assetType,
        components: config.components
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Configuration saved successfully");
      
      // Reload config to verify save worked
      await loadConfig();
      
      if (onConfigSaved) {
        onConfigSaved(config);
      }
    } catch (error) {
      console.error("Error saving config:", error);
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const messages = detail.map(d => d.msg || JSON.stringify(d)).join(', ');
        toast.error(`Validation error: ${messages}`);
      } else {
        toast.error(`Failed to save: ${detail || error.message}`);
      }
    }
    setIsSaving(false);
  };

  const resetToDefaults = async () => {
    if (window.confirm("Are you sure you want to reset to default configuration? This will delete all custom changes.")) {
      try {
        await axios.post(`${API}/asset-configs/${assetType}/reset`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const defaultData = DEFAULT_DATA[assetType] || [];
        setConfig({
          asset_type: assetType,
          components: defaultData,
          is_default: true
        });
        toast.success("Configuration reset to defaults");
        if (onConfigSaved) {
          onConfigSaved({ asset_type: assetType, components: defaultData });
        }
      } catch (error) {
        console.error("Error resetting config:", error);
        toast.error("Failed to reset configuration");
      }
    }
  };

  const toggleComponent = (componentId) => {
    setExpandedComponents(prev => ({
      ...prev,
      [componentId]: !prev[componentId]
    }));
  };

  // Add new failure mode to a component
  const addFailureMode = (componentId) => {
    setConfig(prev => ({
      ...prev,
      components: prev.components.map(comp => 
        comp.id === componentId 
          ? {
              ...comp,
              failureModes: [...comp.failureModes, {
                name: "New Failure Mode",
                description: "",
                importance: "",
                approach: "",
                severity: "Moderate",
                relatedTests: [],
                relatedMonitoring: []
              }]
            }
          : comp
      )
    }));
  };

  // Add new test to a component
  const addTest = (componentId) => {
    setConfig(prev => ({
      ...prev,
      components: prev.components.map(comp => 
        comp.id === componentId 
          ? {
              ...comp,
              tests: [...comp.tests, {
                name: "New Test",
                description: "",
                importance: "",
                approach: "",
                complexity: "Standard",
                cost: "$$",
                detectsFailures: []
              }]
            }
          : comp
      )
    }));
  };

  // Add new monitoring to a component
  const addMonitoring = (componentId) => {
    setConfig(prev => ({
      ...prev,
      components: prev.components.map(comp => 
        comp.id === componentId 
          ? {
              ...comp,
              monitoring: [...comp.monitoring, {
                name: "New Monitoring",
                description: "",
                importance: "",
                approach: "",
                complexity: "Standard",
                cost: "$$",
                detectsFailures: []
              }]
            }
          : comp
      )
    }));
  };

  // Delete an item
  const deleteItem = (componentId, type, index) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      setConfig(prev => ({
        ...prev,
        components: prev.components.map(comp => 
          comp.id === componentId 
            ? {
                ...comp,
                [type]: comp[type].filter((_, i) => i !== index)
              }
            : comp
        )
      }));
    }
  };

  // Update an item
  const updateItem = (componentId, type, index, field, value) => {
    setConfig(prev => ({
      ...prev,
      components: prev.components.map(comp => 
        comp.id === componentId 
          ? {
              ...comp,
              [type]: comp[type].map((item, i) => 
                i === index ? { ...item, [field]: value } : item
              )
            }
          : comp
      )
    }));
  };

  // Toggle relationship between failure mode and test/monitoring
  const toggleRelationship = (componentId, failureModeIndex, targetType, targetName) => {
    setConfig(prev => ({
      ...prev,
      components: prev.components.map(comp => {
        if (comp.id !== componentId) return comp;
        
        const updatedFailureModes = comp.failureModes.map((fm, idx) => {
          if (idx !== failureModeIndex) return fm;
          
          const relationField = targetType === 'test' ? 'relatedTests' : 'relatedMonitoring';
          const currentRelations = fm[relationField] || [];
          
          if (currentRelations.includes(targetName)) {
            return { ...fm, [relationField]: currentRelations.filter(n => n !== targetName) };
          } else {
            return { ...fm, [relationField]: [...currentRelations, targetName] };
          }
        });
        
        return { ...comp, failureModes: updatedFailureModes };
      })
    }));
  };

  // Render item editor - now just shows item with edit button that opens modal
  const renderItemEditor = (componentId, type, item, index) => {
    const itemName = typeof item === 'string' ? item : item.name;
    
    return (
      <div className="flex items-center justify-between p-2 bg-white rounded border hover:bg-slate-50">
        <span className="text-sm">{itemName}</span>
        <div className="flex gap-1">
          <Button 
            size="sm" 
            variant="ghost" 
            className="h-7 w-7 p-0"
            data-testid={`edit-${type}-${index}`}
            onClick={(e) => {
              e.stopPropagation();
              // Only store componentId, type, and index - we'll read item from state fresh
              setEditModalData({ componentId, type, index });
              setEditModalOpen(true);
            }}
          >
            <Edit2 className="w-3 h-3" />
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
            data-testid={`delete-${type}-${index}`}
            onClick={(e) => {
              e.stopPropagation();
              deleteItem(componentId, type, index);
            }}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    );
  };

  // Get type label for modal
  const getTypeLabel = (type) => {
    switch (type) {
      case 'failureModes': return 'Failure Mode';
      case 'tests': return 'Diagnostic Test';
      case 'monitoring': return 'Continuous Monitoring';
      default: return 'Item';
    }
  };

  // Render Edit Modal
  const renderEditModal = () => {
    if (!editModalOpen || !editModalData) return null;
    
    const { componentId, type, index } = editModalData;
    const component = config?.components?.find(c => c.id === componentId);
    
    // Always get the current item from state to ensure we have the latest data
    // This fixes the bug where newly added items couldn't be edited
    const item = component?.[type]?.[index];
    
    // If item doesn't exist (was deleted), close the modal
    if (!item) {
      setEditModalOpen(false);
      return null;
    }
    
    return (
      <div 
        className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4" 
        onClick={(e) => {
          // Only close if clicking directly on the backdrop
          if (e.target === e.currentTarget) {
            setEditModalOpen(false);
          }
        }}
        onPointerDown={(e) => {
          // Prevent pointer events from reaching parent dialog
          e.stopPropagation();
        }}
      >
        <div 
          className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="bg-slate-100 px-6 py-4 border-b flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-lg text-slate-900">
                Edit {getTypeLabel(type)}
              </h3>
              <p className="text-sm text-slate-500">
                Component: {component?.name || 'Unknown'}
              </p>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setEditModalOpen(false)}
              className="h-8 w-8 p-0 rounded-full"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Modal Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="grid grid-cols-2 gap-6">
              {/* Left Column - Basic Info */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Name</Label>
                  <Input 
                    value={item.name || ""} 
                    onChange={(e) => updateItem(componentId, type, index, 'name', e.target.value)}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Description</Label>
                  <Textarea 
                    value={item.description || ""} 
                    onChange={(e) => updateItem(componentId, type, index, 'description', e.target.value)}
                    className="mt-1 min-h-[100px]"
                    placeholder="Detailed description of this item..."
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Why It Matters</Label>
                  <Textarea 
                    value={item.importance || ""} 
                    onChange={(e) => updateItem(componentId, type, index, 'importance', e.target.value)}
                    className="mt-1 min-h-[100px]"
                    placeholder="Explain the importance and impact..."
                  />
                </div>
              </div>
              
              {/* Right Column - Approach & Attributes */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">
                    {type === 'tests' ? 'Testing Procedure' : type === 'monitoring' ? 'How It Works' : 'Detection Approach'}
                  </Label>
                  <Textarea 
                    value={item.approach || ""} 
                    onChange={(e) => updateItem(componentId, type, index, 'approach', e.target.value)}
                    className="mt-1 min-h-[100px]"
                    placeholder="Describe the approach or procedure..."
                  />
                </div>
                
                {/* Attributes based on type */}
                {type === 'failureModes' && (
                  <div>
                    <Label className="text-sm font-medium">Severity</Label>
                    <select
                      value={item.severity || "Moderate"}
                      onChange={(e) => updateItem(componentId, type, index, 'severity', e.target.value)}
                      className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      {SEVERITY_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                {(type === 'tests' || type === 'monitoring') && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Complexity</Label>
                      <select
                        value={item.complexity || "Standard"}
                        onChange={(e) => updateItem(componentId, type, index, 'complexity', e.target.value)}
                        className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        {COMPLEXITY_OPTIONS.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Cost</Label>
                      <select
                        value={item.cost || "$$"}
                        onChange={(e) => updateItem(componentId, type, index, 'cost', e.target.value)}
                        className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        {COST_OPTIONS.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Images Section - Full Width */}
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    Images (up to 10)
                  </Label>
                  <p className="text-xs text-slate-500 mt-1">
                    Upload images or add URLs for the carousel in the detail panel
                  </p>
                </div>
                <div className="flex gap-2">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        const currentImages = item.images || [];
                        if (currentImages.length >= 10) {
                          toast.error("Maximum 10 images allowed");
                          return;
                        }
                        
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        try {
                          const response = await axios.post(`${API}/upload`, formData, {
                            headers: { 
                              Authorization: `Bearer ${token}`,
                              'Content-Type': 'multipart/form-data'
                            }
                          });
                          
                          updateItem(componentId, type, index, 'images', [
                            ...currentImages,
                            { url: response.data.url, caption: file.name }
                          ]);
                          toast.success("Image uploaded successfully");
                        } catch (error) {
                          toast.error("Failed to upload image");
                          console.error(error);
                        }
                        e.target.value = '';
                      }}
                      disabled={(item.images || []).length >= 10}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      asChild
                      disabled={(item.images || []).length >= 10}
                    >
                      <span>
                        <Camera className="w-4 h-4 mr-1" /> Browse File
                      </span>
                    </Button>
                  </label>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const currentImages = item.images || [];
                      if (currentImages.length < 10) {
                        updateItem(componentId, type, index, 'images', [
                          ...currentImages,
                          { url: '', caption: '' }
                        ]);
                      }
                    }}
                    disabled={(item.images || []).length >= 10}
                  >
                    <Link2 className="w-4 h-4 mr-1" /> Add URL
                  </Button>
                </div>
              </div>
              
              {(item.images || []).length === 0 ? (
                <div className="bg-slate-50 rounded-lg p-6 text-center border-2 border-dashed">
                  <Camera className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No custom images added yet</p>
                  <p className="text-xs text-slate-400 mt-1">Click "Browse File" to upload or "Add URL" for external images</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {(item.images || []).map((img, imgIdx) => (
                    <div key={imgIdx} className="flex gap-3 items-start bg-slate-50 p-3 rounded-lg border">
                      <div className="w-16 h-16 bg-slate-200 rounded overflow-hidden flex-shrink-0 relative">
                        {img.url ? (
                          <img 
                            src={img.url.startsWith('/api') ? `${API.replace('/api', '')}${img.url}` : img.url}
                            alt={img.caption || `Image ${imgIdx + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className={`absolute inset-0 flex items-center justify-center ${img.url ? 'hidden' : ''}`}>
                          <ImageIcon className="w-6 h-6 text-slate-400" />
                        </div>
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex gap-2">
                          <Input
                            placeholder="Image URL or upload a file"
                            value={img.url || ''}
                            onChange={(e) => {
                              const newImages = [...(item.images || [])];
                              newImages[imgIdx] = { ...newImages[imgIdx], url: e.target.value };
                              updateItem(componentId, type, index, 'images', newImages);
                            }}
                            className="h-8 text-sm flex-1"
                          />
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                
                                const formData = new FormData();
                                formData.append('file', file);
                                
                                try {
                                  const response = await axios.post(`${API}/upload`, formData, {
                                    headers: { 
                                      Authorization: `Bearer ${token}`,
                                      'Content-Type': 'multipart/form-data'
                                    }
                                  });
                                  
                                  const newImages = [...(item.images || [])];
                                  newImages[imgIdx] = { 
                                    ...newImages[imgIdx], 
                                    url: response.data.url,
                                    caption: newImages[imgIdx].caption || file.name
                                  };
                                  updateItem(componentId, type, index, 'images', newImages);
                                  toast.success("Image uploaded");
                                } catch (error) {
                                  toast.error("Failed to upload");
                                }
                                e.target.value = '';
                              }}
                            />
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" asChild>
                              <span><Camera className="w-4 h-4" /></span>
                            </Button>
                          </label>
                        </div>
                        <Input
                          placeholder="Caption (optional)"
                          value={img.caption || ''}
                          onChange={(e) => {
                            const newImages = [...(item.images || [])];
                            newImages[imgIdx] = { ...newImages[imgIdx], caption: e.target.value };
                            updateItem(componentId, type, index, 'images', newImages);
                          }}
                          className="h-8 text-sm"
                        />
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700 flex-shrink-0"
                        onClick={() => {
                          const newImages = (item.images || []).filter((_, i) => i !== imgIdx);
                          updateItem(componentId, type, index, 'images', newImages);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Modal Footer */}
          <div className="px-6 py-4 border-t bg-slate-50 flex justify-end gap-3">
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => setEditModalOpen(false)}>
              <Check className="w-4 h-4 mr-1" /> Done
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Render Components Tab
  const renderComponentsTab = () => {
    if (!config?.components) return null;

    return (
      <div className="space-y-4">
        {config.components.map((component) => (
          <Card key={component.id} className="border-slate-200">
            <CardHeader 
              className="py-3 px-4 cursor-pointer hover:bg-slate-50"
              onClick={() => toggleComponent(component.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {expandedComponents[component.id] ? 
                    <ChevronDown className="w-4 h-4" /> : 
                    <ChevronRight className="w-4 h-4" />
                  }
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                    style={{ backgroundColor: component.color }}
                  >
                    {component.number}
                  </div>
                  <span className="font-medium">{component.name}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Badge variant="outline">{component.failureModes?.length || 0} Failures</Badge>
                  <Badge variant="outline">{component.tests?.length || 0} Tests</Badge>
                  <Badge variant="outline">{component.monitoring?.length || 0} Monitoring</Badge>
                </div>
              </div>
            </CardHeader>
            
            {expandedComponents[component.id] && (
              <CardContent className="pt-0 px-4 pb-4">
                <div className="grid grid-cols-3 gap-4">
                  {/* Failure Modes Column */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-red-700 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Failure Modes
                      </h4>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-6 text-xs"
                        onClick={() => addFailureMode(component.id)}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add
                      </Button>
                    </div>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {component.failureModes?.map((item, idx) => (
                        <div key={idx}>
                          {renderItemEditor(component.id, 'failureModes', item, idx)}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Diagnostic Tests Column */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-blue-700 flex items-center gap-1">
                        <TestTube className="w-3 h-3" /> Diagnostic Tests
                      </h4>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-6 text-xs"
                        onClick={() => addTest(component.id)}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add
                      </Button>
                    </div>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {component.tests?.map((item, idx) => (
                        <div key={idx}>
                          {renderItemEditor(component.id, 'tests', item, idx)}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Continuous Monitoring Column */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-green-700 flex items-center gap-1">
                        <Activity className="w-3 h-3" /> Continuous Monitoring
                      </h4>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-6 text-xs"
                        onClick={() => addMonitoring(component.id)}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add
                      </Button>
                    </div>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {component.monitoring?.map((item, idx) => (
                        <div key={idx}>
                          {renderItemEditor(component.id, 'monitoring', item, idx)}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>
    );
  };

  // Render Relationships Tab
  const renderRelationshipsTab = () => {
    if (!config?.components) return null;

    const currentComponentId = selectedComponentId || config.components[0]?.id;
    const selectedComponent = config.components.find(c => c.id === currentComponentId);
    if (!selectedComponent) return null;

    return (
      <div className="space-y-4">
        <div>
          <Label htmlFor="component-select">Select Component</Label>
          <select
            id="component-select"
            value={currentComponentId}
            onChange={(e) => setSelectedComponentId(e.target.value)}
            className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            {config.components.map(comp => (
              <option key={comp.id} value={comp.id}>
                {comp.name}
              </option>
            ))}
          </select>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <div className="bg-slate-100 p-3 border-b">
            <h4 className="font-medium text-sm">
              Configure Relationships for: {selectedComponent.name}
            </h4>
            <p className="text-xs text-slate-500 mt-1">
              Select which Tests and Monitoring items are related to each Failure Mode. 
              These will be highlighted when a failure mode is clicked.
            </p>
          </div>
          
          <div className="max-h-[400px] overflow-y-auto">
            {selectedComponent.failureModes?.map((fm, fmIdx) => {
              const fmName = typeof fm === 'string' ? fm : fm.name;
              const relatedTests = fm.relatedTests || [];
              const relatedMonitoring = fm.relatedMonitoring || [];
              
              return (
                <div key={fmIdx} className="border-b last:border-b-0 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <span className="font-medium">{fmName}</span>
                    <Badge variant="destructive" className="text-xs">
                      {fm.severity || "Moderate"}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6 pl-6">
                    {/* Related Tests */}
                    <div>
                      <h5 className="text-xs font-medium text-blue-700 mb-2 flex items-center gap-1">
                        <TestTube className="w-3 h-3" /> Related Tests
                      </h5>
                      <div className="space-y-1">
                        {selectedComponent.tests?.map((test, testIdx) => {
                          const testName = typeof test === 'string' ? test : test.name;
                          const isChecked = relatedTests.includes(testName);
                          return (
                            <label 
                              key={testIdx} 
                              className="flex items-center gap-2 text-sm cursor-pointer hover:bg-blue-50 p-1 rounded"
                            >
                              <Checkbox 
                                checked={isChecked}
                                onCheckedChange={() => toggleRelationship(selectedComponent.id, fmIdx, 'test', testName)}
                              />
                              {testName}
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Related Monitoring */}
                    <div>
                      <h5 className="text-xs font-medium text-green-700 mb-2 flex items-center gap-1">
                        <Activity className="w-3 h-3" /> Related Monitoring
                      </h5>
                      <div className="space-y-1">
                        {selectedComponent.monitoring?.map((mon, monIdx) => {
                          const monName = typeof mon === 'string' ? mon : mon.name;
                          const isChecked = relatedMonitoring.includes(monName);
                          return (
                            <label 
                              key={monIdx} 
                              className="flex items-center gap-2 text-sm cursor-pointer hover:bg-green-50 p-1 rounded"
                            >
                              <Checkbox 
                                checked={isChecked}
                                onCheckedChange={() => toggleRelationship(selectedComponent.id, fmIdx, 'monitoring', monName)}
                              />
                              {monName}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const assetLabel = assetType?.charAt(0).toUpperCase() + assetType?.slice(1) || "Asset";

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" 
        onClose={onClose}
        data-testid="asset-config-modal"
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Asset Configuration - {assetLabel}
          </DialogTitle>
          <DialogDescription>
            Configure components, failure modes, tests, monitoring, and their relationships.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="grid w-full grid-cols-2 flex-shrink-0">
                <TabsTrigger value="components" className="flex items-center gap-2">
                  <Settings className="w-4 h-4" /> Components & Items
                </TabsTrigger>
                <TabsTrigger value="relationships" className="flex items-center gap-2">
                  <Link2 className="w-4 h-4" /> Relationships
                </TabsTrigger>
              </TabsList>

              <div className="flex-1 overflow-y-auto mt-4">
                <TabsContent value="components" className="m-0">
                  {renderComponentsTab()}
                </TabsContent>

                <TabsContent value="relationships" className="m-0">
                  {renderRelationshipsTab()}
                </TabsContent>
              </div>
            </Tabs>

            {/* Footer Actions */}
            <div className="flex items-center justify-between pt-4 border-t mt-4 flex-shrink-0">
              <Button 
                variant="outline" 
                onClick={resetToDefaults}
                className="text-red-600 hover:text-red-700"
              >
                <RotateCcw className="w-4 h-4 mr-2" /> Reset to Defaults
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button onClick={saveConfig} disabled={isSaving}>
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Configuration
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    
    {/* Edit Item Modal - Rendered completely outside main dialog */}
    {renderEditModal()}
  </>
  );
};

export default AssetConfigManager;
